import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Defaults match the historical hardcoded values so the UI never flickers
// or shows blank if the network is slow / DB row is missing.
export const SITE_SETTING_DEFAULTS = {
  phone_display: "856-200-3127",
  phone_tel: "8562003127",
  email: "info@cherryhillrecoverycenter.com",
  address: "1930 Marlton Pike East\nCherry Hill, NJ 08003",
  hero_headline_prefix: "Cherry Hill Drug & Alcohol Rehab",
  hero_headline_accent: "",
  hero_subheadline:
    "Start drug or alcohol rehab in Cherry Hill with a free, confidential assessment. PHP, IOP, MAT, detox, and dual-diagnosis care — most insurance accepted. Call 856-200-3127, 24/7.",
} as const;

export type SiteSettingKey = keyof typeof SITE_SETTING_DEFAULTS;
export type SiteSettings = Record<SiteSettingKey, string>;

let cache: SiteSettings | null = null;
let inflight: Promise<SiteSettings> | null = null;
const listeners = new Set<(s: SiteSettings) => void>();

async function fetchSettings(): Promise<SiteSettings> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const { data } = await supabase
      .from("site_settings")
      .select("key, value");
    const merged: SiteSettings = { ...SITE_SETTING_DEFAULTS };
    for (const row of data ?? []) {
      if (row.key in merged && typeof row.value === "string" && row.value.length > 0) {
        (merged as any)[row.key] = row.value;
      }
    }
    cache = merged;
    listeners.forEach((l) => l(merged));
    return merged;
  })();
  return inflight;
}

export function refreshSiteSettings() {
  cache = null;
  inflight = null;
  return fetchSettings();
}

export function useSiteSettings(): SiteSettings {
  const [settings, setSettings] = useState<SiteSettings>(cache ?? SITE_SETTING_DEFAULTS);
  useEffect(() => {
    let mounted = true;
    fetchSettings().then((s) => mounted && setSettings(s));
    const l = (s: SiteSettings) => mounted && setSettings(s);
    listeners.add(l);
    return () => {
      mounted = false;
      listeners.delete(l);
    };
  }, []);
  return settings;
}
